/** A read-only picture of the wizard's routes: every `next`, case, `else`,
    `on_return`, `on_issue`, `goto` and repeat body (design §13). Laid out in
    layers by distance from `start`; the boxes and arrows are the process-flow
    diagram's own (`txn/flow/shapes`) so a wizard reads like the rest of the
    product. Click a box to inspect that step; a `call` box links to the child
    wizard's designer. */

import { useMemo } from 'react'
import {
  EDGE_STYLE, EdgeArrowDefs, GridBackdrop, NODE_H, NODE_W, NodeOutline, clip, edgeGeometry,
} from '@twasta/page-runtime/txn/flow/shapes'
import type { NodeKind } from '@twasta/page-runtime/txn/flow'
import { useWizardDesign } from './context'
import type { EdgeKind, WizardStep } from './types'
import { KIND_META, flowEdges } from './types'

const GAP_X = 70
const GAP_Y = 34
const PAD = 24
const FINISH = '__finish'

const SHAPE: Partial<Record<WizardStep['kind'], NodeKind>> = {
  call: 'process', handoff: 'process', save: 'store', branch: 'entity', auto: 'external',
  fetch_url: 'external', load: 'entity', repeat: 'entity',
}

const EDGE_OF: Record<EdgeKind, keyof typeof EDGE_STYLE> = {
  next: 'work', case: 'link', else: 'link', goto: 'read', return: 'update', issue: 'approval', body: 'reference',
}

interface Placed { id: string; x: number; y: number; step: WizardStep | null }

export default function FlowGraph({ selected, onSelect }: {
  selected: string | null
  onSelect: (id: string) => void
}) {
  const { model, project } = useWizardDesign()
  const { nodes, edges, width, height } = useMemo(() => {
    const edges = flowEdges(model).filter((e) => e.from !== e.to)
    const byId = new Map(model.steps.map((s) => [s.id, s]))
    // Implicit "following step" when a step has no route of its own.
    model.steps.forEach((s, i) => {
      const has = edges.some((e) => e.from === s.id && (e.kind === 'next' || e.kind === 'case' || e.kind === 'else'))
      if (!has && s.kind !== 'branch' && model.steps[i + 1]) {
        edges.push({ from: s.id, to: model.steps[i + 1].id, kind: 'next', label: '(following)' })
      }
    })
    const ids = [...model.steps.map((s) => s.id)]
    if (edges.some((e) => e.to === FINISH)) ids.push(FINISH)
    // Layer = shortest distance from start (BFS); unreachable steps last.
    const layer = new Map<string, number>()
    const start = byId.has(model.start) ? model.start : ids[0]
    if (start) {
      layer.set(start, 0)
      const queue = [start]
      while (queue.length) {
        const cur = queue.shift()!
        for (const e of edges) {
          if (e.from !== cur || layer.has(e.to) || !ids.includes(e.to)) continue
          layer.set(e.to, (layer.get(cur) || 0) + 1)
          queue.push(e.to)
        }
      }
    }
    const maxLayer = Math.max(0, ...layer.values())
    if (layer.has(FINISH)) layer.set(FINISH, maxLayer + 1)
    const lastLayer = Math.max(0, ...layer.values()) + 1
    for (const id of ids) if (!layer.has(id)) layer.set(id, lastLayer)
    const columns = new Map<number, string[]>()
    for (const id of ids) {
      const l = layer.get(id) || 0
      if (!columns.has(l)) columns.set(l, [])
      columns.get(l)!.push(id)
    }
    const nodes: Placed[] = []
    let maxRows = 0
    for (const [l, col] of [...columns.entries()].sort((a, b) => a[0] - b[0])) {
      maxRows = Math.max(maxRows, col.length)
      col.forEach((id, row) => nodes.push({
        id, step: byId.get(id) || null,
        x: PAD + l * (NODE_W + GAP_X), y: PAD + row * (NODE_H + GAP_Y),
      }))
    }
    const cols = Math.max(1, ...[...columns.keys()].map((k) => k + 1))
    return {
      nodes,
      edges: edges.filter((e) => ids.includes(e.to)),
      width: PAD * 2 + cols * NODE_W + (cols - 1) * GAP_X,
      height: PAD * 2 + Math.max(1, maxRows) * NODE_H + (Math.max(1, maxRows) - 1) * GAP_Y,
    }
  }, [model])

  const at = new Map(nodes.map((n) => [n.id, n]))
  if (!model.steps.length) {
    return <div style={{ padding: 16, fontSize: 13, color: 'var(--color-text-secondary)' }}>No steps yet.</div>
  }
  return (
    <div style={{ overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)' }}>
      {/* maxWidth none: the app's global `svg { max-width: 100% }` would squeeze
          the drawing to the panel and clip every step past the first few —
          the frame above scrolls instead. */}
      <svg width={width} height={height} role="img" aria-label="Wizard flow"
        style={{ maxWidth: 'none', display: 'block' }}>
        <defs>
          <EdgeArrowDefs prefix="wz" />
          <GridBackdrop id="wz-grid" />
        </defs>
        <rect width={width} height={height} fill="url(#wz-grid)" />
        {edges.map((e, i) => {
          const a = at.get(e.from)
          const b = at.get(e.to)
          if (!a || !b) return null
          const g = edgeGeometry(a, b)
          const st = EDGE_STYLE[EDGE_OF[e.kind]]
          return (
            <g key={i}>
              <path d={g.d} fill="none" stroke={st.stroke} strokeWidth={st.width} strokeDasharray={st.dash}
                markerEnd={`url(#wz-${EDGE_OF[e.kind]})`} opacity={0.9} />
              {e.label && (
                <text x={g.mid.x} y={g.mid.y - 4} textAnchor="middle" fontSize={10}
                  fill="var(--color-text-secondary)" style={{ paintOrder: 'stroke', stroke: 'var(--color-bg)', strokeWidth: 3 }}>
                  {clip(e.label, 28)}
                </text>
              )}
            </g>
          )
        })}
        {nodes.map((n) => {
          const isFinish = n.id === FINISH
          const kind: NodeKind = isFinish ? 'entity' : (n.step && SHAPE[n.step.kind]) || 'step'
          const sel = n.id === selected
          const isStart = n.id === model.start
          const meta = n.step ? KIND_META[n.step.kind] : null
          const child = n.step && (n.step.kind === 'call' || n.step.kind === 'handoff') ? n.step.wizard : null
          return (
            <g key={n.id} style={{ cursor: isFinish ? 'default' : 'pointer' }}
              onClick={() => { if (!isFinish) onSelect(n.id) }}>
              <NodeOutline kind={kind} x={n.x} y={n.y}
                stroke={sel ? 'var(--color-accent)' : isStart ? 'var(--color-success, #15803d)' : 'var(--color-border-strong, #c7cfd9)'}
                fill={sel ? 'var(--color-bg-secondary)' : 'var(--color-bg)'} width={sel ? 2.4 : 1.4} />
              <text x={n.x + 12} y={n.y + 20} fontSize={12} fontWeight={600} fill="var(--color-text)">
                {isFinish ? 'Finish' : clip(n.step?.title || n.id, 22)}
              </text>
              {!isFinish && (
                <text x={n.x + 12} y={n.y + 38} fontSize={10.5} fill="var(--color-text-secondary)">
                  {clip(`${meta?.label || n.step?.kind} · ${n.id}${isStart ? ' · start' : ''}`, 30)}
                </text>
              )}
              {child && (
                <a href={`/designers/wizard/${encodeURIComponent(project)}/${encodeURIComponent(child)}`}
                  target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()}>
                  <text x={n.x + NODE_W - 10} y={n.y + 20} fontSize={10.5} textAnchor="end"
                    fill="var(--color-accent)" style={{ textDecoration: 'underline' }}>
                    {clip(child, 14)} ↗
                  </text>
                </a>
              )}
            </g>
          )
        })}
      </svg>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', padding: '6px 10px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
        {([['next', 'next'], ['case', 'condition'], ['return', 'call returns'], ['goto', 'goto / tool'], ['issue', 'issue / error'], ['body', 'repeat']] as const).map(([k, label]) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <svg width={22} height={6}><line x1={0} y1={3} x2={22} y2={3} stroke={EDGE_STYLE[EDGE_OF[k]].stroke}
              strokeWidth={2} strokeDasharray={EDGE_STYLE[EDGE_OF[k]].dash} /></svg>
            {label}
          </span>
        ))}
      </div>
    </div>
  )
}
